import { RhInput } from "@rhythm-ui/react";
import React, { forwardRef, useEffect, useRef, useState } from "react";

// Helper type to extract props from a component
type ExtractProps<T> = T extends React.ComponentType<infer P> ? P : never;

// Infer RhInput props, falling back to a default type if inference fails
type InferredRhInputProps =
  ExtractProps<typeof RhInput> extends never
    ? React.InputHTMLAttributes<HTMLInputElement> &
        React.TextareaHTMLAttributes<HTMLTextAreaElement>
    : ExtractProps<typeof RhInput>;

type DebouncedRhInputProps = Omit<InferredRhInputProps, "onChange"> & {
  onChange: (value: string) => void;
  debounceTime?: number;
};

export const DebouncedRhInput = forwardRef<
  HTMLInputElement & HTMLTextAreaElement,
  DebouncedRhInputProps
>(({ value, onChange, debounceTime = 300, ...rest }, ref) => {
  const [internalValue, setInternalValue] = useState(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debouncedOnChange = (newValue: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, debounceTime);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>,
  ) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    debouncedOnChange(newValue);
  };

  return (
    <RhInput
      // The red squiggly originates from rhinput type
      // being defined incorrectly. "HTMLInputElement & HTMLTextAreaElement" :/
      ref={ref}
      value={internalValue}
      onChange={handleChange}
      {...rest}
    />
  );
});

DebouncedRhInput.displayName = "DebouncedRhInput";
